import {
  BankOutlined,
  CheckCircleFilled,
  DownOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input, Spin, Tag } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import {
  getSelectedTenant,
  getTenantMemberships,
  saveSelectedTenant,
} from 'services/tenant/tenantMembershipService';

const STATUS_LABELS = {
  active: 'Active',
  pending: 'Pending',
  'temporary-access': 'Temporary Access',
  provisioning: 'Provisioning',
  expired: 'Expired',
  inactive: 'Inactive',
  suspended: 'Suspended',
};

const isUnavailable = (tenant) =>
  ['inactive', 'suspended'].includes(tenant.status);

export default function TenantSwitcher() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(getSelectedTenant);
  const navigate = useNavigate();

  useEffect(() => {
    getTenantMemberships()
      .then((memberships) => {
        setTenants(memberships);
        setSelectedTenant((current) =>
          current || memberships.find((tenant) => !isUnavailable(tenant)) || null
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredTenants = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return tenants;

    return tenants.filter((tenant) =>
      [tenant.name, tenant.identifier, tenant.plan, tenant.role]
        .some((value) => value.toLowerCase().includes(normalizedQuery))
    );
  }, [query, tenants]);

  const switchTenant = (tenant) => {
    if (isUnavailable(tenant) || tenant.id === selectedTenant?.id) return;

    saveSelectedTenant(tenant);
    setSelectedTenant(tenant);
    setOpen(false);
    window.location.reload();
  };

  const openTenantSelection = () => {
    setOpen(false);
    navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.tenantSelection}?mode=manage`);
  };

  const dropdownContent = (
    <div className="tenant-switcher-dropdown">
      <Input
        prefix={<SearchOutlined />}
        placeholder="Search organization..."
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onClick={(event) => event.stopPropagation()}
        allowClear
      />

      <div className="tenant-switcher-list">
        {loading ? (
          <div className="tenant-switcher-loading"><Spin size="small" /></div>
        ) : filteredTenants.map((tenant) => {
          const selected = tenant.id === selectedTenant?.id;
          const unavailable = isUnavailable(tenant);

          return (
            <button
              type="button"
              className={`tenant-switcher-option${selected ? ' tenant-switcher-option--selected' : ''}`}
              key={tenant.id}
              disabled={unavailable}
              onClick={() => switchTenant(tenant)}
            >
              <span className="tenant-switcher-option-icon"><BankOutlined /></span>
              <span className="tenant-switcher-option-content">
                <strong>{tenant.name}</strong>
                <small>{tenant.identifier}</small>
                <span>{tenant.plan} <i /> Your Role: {tenant.role}</span>
              </span>
              <span className="tenant-switcher-option-status">
                <Tag className={`tenant-switcher-status tenant-switcher-status--${tenant.status}`}>
                  {STATUS_LABELS[tenant.status]}
                </Tag>
                {selected && <CheckCircleFilled />}
              </span>
            </button>
          );
        })}
      </div>

      <div className="tenant-switcher-footer">
        <Button type="text" block icon={<PlusOutlined />} onClick={openTenantSelection}>
          Request Access to Organization
        </Button>
        <Button type="text" block icon={<SettingOutlined />} onClick={openTenantSelection}>
          Manage Organizations
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomLeft"
      dropdownRender={() => dropdownContent}
    >
      <Button className="tenant-switcher-trigger">
        <BankOutlined />
        <span>{selectedTenant?.name || 'Select Organization'}</span>
        <DownOutlined />
      </Button>
    </Dropdown>
  );
}
